import React, { useState, useEffect, useRef } from 'react';
import { HiUsers } from 'react-icons/hi2';
import { IoChevronDown } from 'react-icons/io5';
import { MdBookmark, MdBookmarkBorder } from 'react-icons/md';
import { BsEmojiSmile } from 'react-icons/bs';
import { FaTrophy } from 'react-icons/fa';
import { IoTrashOutline } from 'react-icons/io5';
import { IniciativaFormatada } from 'types/iniciativa';
import { ModalIniciativa } from '../../Iniciativas/components/ModalIniciativa';
import { useAuth } from 'context/AuthContext';
import {
  registrarElogio,
  registrarFavorito,
  registrarIniciativas
} from 'services/iniciativa-action.service';
import { getCategoriaCorHex } from 'utils/iniciativa-categoria';
import iniciativaVideoImg from 'assets/img/2026/iniciativa_video.png';
import './IniciativaCardEtapa3.scss';

interface VotarConfig {
  votada: boolean;
  onVotar?: () => void;
  disabled?: boolean;
  /** Mensagem exibida no estado desabilitado (ex.: limite atingido). */
  disabledLabel?: string;
  readonly?: boolean;
}

type IniciativaCardProps = IniciativaFormatada & {
  /** Quando informado, substitui o botão "VER MAIS DETALHES" por controles de votação. */
  votar?: VotarConfig;
};

export const IniciativaCardEtapa3: React.FC<IniciativaCardProps> = (props) => {
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isComplimentOpen, setIsComplimentOpen] = useState(false);
  const [selectedCompliment, setSelectedCompliment] = useState<string>(props.usuarioElogio || '');
  const complimentRef = useRef<HTMLDivElement>(null);

  // Estados para controle de ações
  const [isFavorited, setIsFavorited] = useState(props.usuarioFavoritou);
  const [loadingAction, setLoadingAction] = useState(false);

  const complimentOptions = ['Arrasou!', 'Iniciativa inspiradora!', 'Superou expectativas!'];

  // Fecha o dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (complimentRef.current && !complimentRef.current.contains(event.target as Node)) {
        setIsComplimentOpen(false);
      }
    };

    if (isComplimentOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isComplimentOpen]);

  const handleComplimentSelect = async (compliment: string) => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      setSelectedCompliment(compliment);
      setIsComplimentOpen(false);

      await registrarElogio(user._id, {
        id_iniciativa: props.id,
        elogio: compliment
      });
    } catch (error) {
      setSelectedCompliment('');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user?._id || loadingAction) return;

    try {
      setLoadingAction(true);
      const novoStatus = !isFavorited;

      await registrarFavorito(user._id, {
        id_iniciativa: props.id,
        ativo: novoStatus
      });

      setIsFavorited(novoStatus);
    } catch (error) {
      // Erro silencioso
    } finally {
      setLoadingAction(false);
    }
  };

  const handleOpenDetails = async () => {
    setIsModalOpen(true);

    if (!user?._id) {
      return;
    }

    try {
      await registrarIniciativas(user._id);
    } catch (error) {
    }
  };

  const {
    titulo,
    descricao,
    autores,
    categoria,
    corCategoria,
    categoryPremio,
    imagem,
    videoUrl,
    originalVideoUrl,
    classificadaEtapa1,
    classificadaEtapa2,
  } = props;

  const corCategoriaHex = getCategoriaCorHex(categoryPremio);

  // No modo de votação não exibimos a moldura "Classificada Etapa X" — a faixa
  // amarela inferior já comunica o estado da iniciativa. Etapa 2 tem precedência
  // sobre Etapa 1 quando ambas estão marcadas.
  const isVotingMode = Boolean(props.votar);
  const showClassificadaEtapa2 = Boolean(classificadaEtapa2) && !isVotingMode;
  const showClassificadaEtapa1 =
    Boolean(classificadaEtapa1) && !classificadaEtapa2 && !isVotingMode;
  const showClassificada = showClassificadaEtapa1 || showClassificadaEtapa2;
  const cardClassName = [
    'iniciativa-card',
    showClassificada ? 'iniciativa-card--classificada' : '',
    showClassificadaEtapa2 ? 'iniciativa-card--classificada-etapa2' : '',
    props.votar?.votada ? 'iniciativa-card--voted' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const tagElement = (
    <div
      className={`iniciativa-card__tag tag-${corCategoria}`}
      style={{ backgroundColor: corCategoriaHex }}
      title={categoria}
    >
      {categoria}
    </div>
  );

  const mediaElement = videoUrl ? (
    <a
      href={originalVideoUrl || videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="iniciativa-card__video-link"
    >
      <img
        src={iniciativaVideoImg}
        alt="Clique para assistir ao vídeo da iniciativa"
        className="iniciativa-card__image"
      />
    </a>
  ) : (
    <img
      src={imagem}
      alt={`Imagem da iniciativa ${titulo}`}
      className="iniciativa-card__image"
    />
  );

  return (
    <>
      <div className={cardClassName} role="article" aria-label={`Iniciativa: ${titulo}`}>
        {showClassificada ? (
          <div className="iniciativa-card__media-frame">
            <div className="iniciativa-card__classificada-header">
              <span className="iniciativa-card__classificada-label">
                <FaTrophy size={20} aria-hidden="true" />
                {showClassificadaEtapa2 ? 'CLASSIFICADA ETAPA 2' : 'CLASSIFICADA ETAPA 1'}
              </span>
              {tagElement}
            </div>
            <div className="iniciativa-card__image-container">{mediaElement}</div>
          </div>
        ) : (
          <div className="iniciativa-card__image-container">
            {mediaElement}
            {tagElement}
          </div>
        )}

        <div className="iniciativa-card__content">
          <h4 className="iniciativa-card__title">{titulo}</h4>
          <p className="iniciativa-card__text">{descricao}</p>

          <div className="iniciativa-card__author" tabIndex={0} aria-label={`Autores: ${autores}`}>
            <HiUsers size={16} aria-hidden="true" />
            <span>Autores: {autores}</span>
          </div>

          <div className="iniciativa-card__action-bar">
            <div className="iniciativa-card__action-left">
              <div className="iniciativa-card__icon-wrapper">
                <BsEmojiSmile size={20} aria-hidden="true" style={{ color: selectedCompliment ? '#5D5FEF' : '#666666' }} />
                {props.totalElogios > 0 && (
                  <span className="iniciativa-card__badge" aria-label={`${props.totalElogios} elogios`}>{props.totalElogios}</span>
                )}
              </div>
              <div className="iniciativa-card__compliment-container" ref={complimentRef}>
                <button
                  className="iniciativa-card__compliment-dropdown"
                  onClick={() => !loadingAction && setIsComplimentOpen(!isComplimentOpen)}
                  aria-expanded={isComplimentOpen}
                  aria-haspopup="listbox"
                  aria-label={selectedCompliment ? `Elogio selecionado: ${selectedCompliment}` : 'Faça um elogio'}
                  disabled={loadingAction}
                  style={{
                    borderColor: selectedCompliment ? '#5D5FEF' : '#E0E7FF',
                    color: selectedCompliment ? '#5D5FEF' : '#666666'
                  }}
                >
                  <span>{selectedCompliment || 'Faça um elogio!'}</span>
                  <IoChevronDown size={16} aria-hidden="true" />
                </button>
                {isComplimentOpen && !loadingAction && (
                  <div className="iniciativa-card__compliment-menu" role="listbox" aria-label="Opções de elogio">
                    {complimentOptions.map((compliment) => (
                      <button
                        key={compliment}
                        className="iniciativa-card__compliment-option"
                        role="option"
                        aria-selected={selectedCompliment === compliment}
                        onClick={() => handleComplimentSelect(compliment)}
                      >
                        {compliment}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="iniciativa-card__action-right">
              <div className="iniciativa-card__icon-wrapper">
                <button
                  className="iniciativa-card__icon-btn"
                  aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar nos favoritos'}
                  onClick={handleToggleFavorite}
                  disabled={loadingAction}
                  aria-pressed={isFavorited}
                  style={{
                    opacity: loadingAction ? 0.6 : 1,
                    color: isFavorited ? '#5D5FEF' : '#666666'
                  }}
                >
                  {isFavorited ? <MdBookmark size={20} aria-hidden="true" /> : <MdBookmarkBorder size={20} aria-hidden="true" />}
                </button>
                {props.totalFavoritos > 0 && (
                  <span className="iniciativa-card__badge" aria-label={`${props.totalFavoritos} favoritos`}>{props.totalFavoritos}</span>
                )}
              </div>
            </div>
          </div>

          <button
            className="iniciativa-card__button"
            onClick={handleOpenDetails}
          >
            VER MAIS DETALHES
          </button>
        </div>

        {props.votar && (
          <div
            className={`iniciativa-card__vote-strip${props.votar.votada ? ' is-voted' : ''}${props.votar.disabled && !props.votar.votada ? ' is-disabled' : ''}${props.votar.readonly ? ' is-readonly' : ''}`}
          >
            {props.votar.votada ? (
              <>
                <span className="iniciativa-card__vote-strip-label">
                  Você votou nesta iniciativa!
                </span>
                {!props.votar.readonly && (
                  <button
                    type="button"
                    className="iniciativa-card__vote-strip-action"
                    onClick={props.votar.onVotar}
                  >
                    REMOVER VOTO
                    <IoTrashOutline size={16} aria-hidden="true" />
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="iniciativa-card__vote-strip-action iniciativa-card__vote-strip-action--full"
                onClick={props.votar.onVotar}
                disabled={props.votar.disabled}
                aria-pressed={false}
              >
                {props.votar.disabled
                  ? (props.votar.disabledLabel || 'LIMITE ATINGIDO')
                  : 'VOTAR'}
              </button>
            )}
          </div>
        )}
      </div>

      <ModalIniciativa
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        iniciativa={props}
      />
    </>
  );
};
